import { jsx, jsxs } from "react/jsx-runtime";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { ChartsDataProvider } from "@mui/x-charts/ChartsDataProvider";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { barClasses, BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, AreaPlot, MarkPlot, LineHighlightPlot, FocusedLineMark } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";
import { useMemo } from "react";
import { transposeRowsToSeries } from "./seriesMapper.js";
import { makeValueFormatter, getYAxisWidth } from "../utils/data.js";
const MixedChart = ({
  title,
  labels = {},
  chartProps,
  dataset,
  height,
  showLabels = false,
  isLoading = false,
  colorScheme = "sand"
}) => {
  const theme = useTheme();
  const xKey = chartProps.configuration.x.data.key;
  const {
    data,
    columns
  } = dataset ?? {
    data: [],
    columns: []
  };
  const rawSeries = useMemo(() => {
    if (!dataset?.data?.length || !columns?.length || isLoading) return [];
    return transposeRowsToSeries(chartProps, data, columns, labels);
  }, [chartProps, data, columns, labels, dataset?.data?.length, isLoading]);
  const {
    chartSeries: chartSeries_0,
    barIds: barIds_0,
    areaIds: areaIds_0
  } = useMemo(() => {
    let colorIdx = 0;
    const barIds = [];
    const areaIds = [];
    const chartSeries = rawSeries.map(s => {
      const col = columns.find(c => c.key === s.columnKey);
      const valueFormatter = makeValueFormatter(col);
      if (s.type === "line") {
        const id = `line-${colorIdx}`;
        const color = theme.charts.line.colors[colorScheme ?? "sand"]?.[colorIdx] ?? "";
        if (s.area) areaIds.push(id);
        colorIdx++;
        return {
          type: "line",
          id,
          data: s.data,
          label: s.label,
          area: s.area ?? false,
          showMark: false,
          color,
          ...(valueFormatter ? {
            valueFormatter
          } : {}),
          ...(s.yAxisId ? {
            yAxisId: s.yAxisId
          } : {})
        };
      }
      const id_0 = `bar-${colorIdx}`;
      barIds.push(id_0);
      const barColorName = theme.charts.bar.colors[colorScheme ?? "sand"]?.[colorIdx] ?? "sand";
      const barColor = theme.charts.bar.gradients[barColorName][0]?.color ?? "";
      colorIdx++;
      return {
        type: "bar",
        id: id_0,
        data: s.data,
        label: s.label,
        ...(s.stack ? {
          stack: s.stack
        } : {}),
        color: barColor,
        ...(valueFormatter ? {
          valueFormatter
        } : {}),
        ...(s.yAxisId ? {
          yAxisId: s.yAxisId
        } : {})
      };
    });
    return {
      chartSeries,
      barIds,
      areaIds
    };
  }, [rawSeries, columns, theme, colorScheme]);
  const yAxes = useMemo(() => {
    const hasRight = rawSeries.some(s_0 => s_0.yAxisId === "right");
    const axisFormatter = subset => makeValueFormatter(columns.find(c_0 => c_0.key === subset[0]?.columnKey), 0);
    const calcWidth = subset_0 => {
      const allValues = subset_0.flatMap(s_1 => s_1.data).filter(v => typeof v === "number");
      if (!allValues.length) return 0;
      const max = Math.ceil(Math.max(...allValues));
      const formatter = axisFormatter(subset_0);
      const repr = formatter ? formatter(max) : max.toString();
      return getYAxisWidth(repr.length);
    };
    if (!hasRight) {
      return [{
        id: "left",
        position: showLabels ? "left" : "none",
        width: showLabels ? calcWidth(rawSeries) : 0,
        valueFormatter: axisFormatter(rawSeries)
      }];
    }
    const leftSeries = rawSeries.filter(s_2 => s_2.yAxisId !== "right");
    const rightSeries = rawSeries.filter(s_3 => s_3.yAxisId === "right");
    return [{
      id: "left",
      position: showLabels ? "left" : "none",
      width: showLabels ? calcWidth(leftSeries) : 0,
      valueFormatter: axisFormatter(leftSeries)
    }, {
      id: "right",
      position: showLabels ? "right" : "none",
      width: showLabels ? calcWidth(rightSeries) : 0,
      valueFormatter: axisFormatter(rightSeries)
    }];
  }, [rawSeries, columns, showLabels]);
  if (!dataset || isLoading || !dataset?.data || dataset?.data.length === 0) {
    return /* @__PURE__ */jsx(Skeleton, {
      variant: "rectangular",
      width: "100%",
      height: 260
    });
  }
  const xIndex = columns.findIndex(col_0 => col_0.key === xKey);
  if (xIndex === -1 && columns.length) throw new Error(`X-axis key '${xKey}' not found in dataset columns`);
  const xAxisLabels = Array.from(new Set(data.map(row => String(row[xIndex]))));
  const surfaceSx = {
    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
      display: "none"
    },
    "& .MuiChartsAxis-label": {
      display: "none"
    },
    ...Object.fromEntries([...barIds_0.map(id_1 => [`& .${barClasses.series}[data-series="${id_1}"] .MuiBarElement-root`, {
      fill: `url(#${colorScheme}-bar-gradient)`
    }]), ...areaIds_0.map(id_2 => [`& .MuiAreaElement-series-${id_2}`, {
      opacity: 0.3,
      fill: `url(#${colorScheme}-line-gradient)`
    }])])
  };
  return /* @__PURE__ */jsxs(Stack, {
    sx: {
      width: "100%"
    },
    children: [/* @__PURE__ */jsx(Stack, {
      sx: {
        padding: theme.sizes.spacing["01"],
        margin: theme.sizes.spacing["01"],
        position: "relative"
      },
      children: /* @__PURE__ */jsxs(ChartsDataProvider, {
        series: chartSeries_0,
        xAxis: [{
          id: "x",
          scaleType: "band",
          data: xAxisLabels,
          categoryGapRatio: 0.35,
          position: showLabels ? "bottom" : "none"
        }],
        yAxis: yAxes,
        height: height - 4,
        margin: {
          right: 0,
          left: 0,
          top: 4,
          bottom: 0
        },
        children: [/* @__PURE__ */jsxs(ChartsSurface, {
          sx: surfaceSx,
          children: [/* @__PURE__ */jsx(ChartsGrid, {
            horizontal: true
          }), /* @__PURE__ */jsx(ChartsAxisHighlight, {
            x: "band"
          }), /* @__PURE__ */jsx(BarPlot, {
            borderRadius: 6,
            skipAnimation: true
          }), /* @__PURE__ */jsx(LinePlot, {
            skipAnimation: true
          }), /* @__PURE__ */jsx(AreaPlot, {
            skipAnimation: true
          }), /* @__PURE__ */jsx(MarkPlot, {}), /* @__PURE__ */jsx(LineHighlightPlot, {}), /* @__PURE__ */jsx(FocusedLineMark, {}), /* @__PURE__ */jsx(ChartsXAxis, {
            axisId: "x"
          }), yAxes.map(axis => /* @__PURE__ */jsx(ChartsYAxis, {
            axisId: axis.id
          }, axis.id)), /* @__PURE__ */jsx(ChartsTooltip, {
            trigger: "axis"
          })]
        }), showLabels && /* @__PURE__ */jsx(ChartsLegend, {
          direction: "horizontal",
          sx: {
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            maxWidth: "80%",
            mx: "auto"
          }
        })]
      })
    }), /* @__PURE__ */jsx(Typography, {
      sx: {
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 600,
        opacity: 0.5
      },
      children: title
    })]
  });
};
export { MixedChart as default };